/**
 * Gobodo Validation Rule Service
 *
 * CRUD for ValidationRule__c records.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig from '../../config/gobodoConfig';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { serializeGobodoData } from '../providers/gobodoRoleProvider';
import { objectNameToApiName } from './gobodoTabConfigService';
import type { ValidationRule, ValidationCondition } from '../queries/validationRuleQueries';

const OBJECT_KEY = 'ValidationRule__c';

function toIsoString(val: any): string {
  if (val == null) return new Date().toISOString();
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && typeof val.toISOString === 'function') return val.toISOString();
  return new Date().toISOString();
}

function parseJson<T>(val: any, fallback: T): T {
  if (val == null) return fallback;
  if (typeof val === 'string' && val) {
    try {
      return JSON.parse(val) as T;
    } catch {
      return fallback;
    }
  }
  if (Array.isArray(val)) return val as T;
  return fallback;
}

function gobodoToValidationRule(record: any): ValidationRule {
  const raw = record.properties || record.data || {};
  const data = serializeGobodoData(raw) as Record<string, any>;
  const conditions = parseJson<ValidationCondition[]>(data.conditionsJson, []);
  return {
    id: String(record.id),
    name: data.name || '',
    objectName: data.objectTypeKey || '',
    description: data.description || undefined,
    isActive: Boolean(data.isActive ?? true),
    conditions,
    logicOperator: (data.logicOperator === 'OR' ? 'OR' : 'AND') as 'AND' | 'OR',
    errorMessage: data.errorMessage || '',
    errorLocation: (data.errorLocation === 'field' ? 'field' : 'top') as 'top' | 'field',
    errorFieldId: data.errorFieldId || undefined,
    createdBy: String(record.creatorid || data.createdBy || 'system'),
    createdDate: toIsoString(record.creationdate ?? data.createdAt),
    lastModified: toIsoString(record.modificationdate ?? data.updatedAt),
    lastModifiedBy: String(data.updatedBy || record.creatorid || 'system'),
  };
}

function validationRuleToGobodoProps(rule: Partial<ValidationRule>): Record<string, any> {
  return {
    objectTypeKey: rule.objectName || '',
    name: rule.name || '',
    description: rule.description ?? '',
    conditionsJson: rule.conditions ? JSON.stringify(rule.conditions) : '[]',
    logicOperator: rule.logicOperator ?? 'AND',
    errorMessage: rule.errorMessage ?? '',
    errorLocation: rule.errorLocation ?? 'top',
    errorFieldId: rule.errorFieldId ?? '',
    isActive: rule.isActive ?? true,
    createdBy: rule.createdBy || 'system',
    updatedBy: rule.lastModifiedBy || rule.createdBy || 'system',
  };
}

export async function fetchValidationRules(objectTypeKey: string): Promise<ValidationRule[]> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const apiName = objectNameToApiName(objectTypeKey);
  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const allRecords = await retryWithBackoff(
    () =>
      objectsApi.getObjectsInGraphByType(GobodoConfig.graph(), OBJECT_KEY, { page: 0, size: 100 }),
    { maxRetries: 2 }
  );

  const records = (allRecords || []).filter((r: any) => {
    const p = serializeGobodoData(r.properties || r.data || {}) as Record<string, any>;
    return (p.objectTypeKey || '') === apiName;
  });

  return records
    .map((r: any) => gobodoToValidationRule(r))
    .sort((a: ValidationRule, b: ValidationRule) => a.lastModified.localeCompare(b.lastModified));
}

export async function fetchValidationRule(id: string): Promise<ValidationRule> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const record = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  return gobodoToValidationRule(record);
}

export async function createValidationRule(
  data: Omit<ValidationRule, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>
): Promise<ValidationRule> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  await ensureObjectTypeExists(OBJECT_KEY, {
    label: 'Validation Rule',
    pluralLabel: 'Validation Rules',
    description: 'Validation rule configuration per object',
    icon: 'Shield',
    category: 'system',
  });

  const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);
  const objectType = await retryWithBackoff(() => objectTypesApi.getObjectTypeByKey(OBJECT_KEY, { maxFields: 0 }), {
    maxRetries: 3,
  });

  const apiName = objectNameToApiName(data.objectName);
  const props = validationRuleToGobodoProps({
    ...data,
    objectName: apiName,
    createdBy: 'Current User',
    lastModifiedBy: 'Current User',
  });

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = objectType.id as any;
  payload.label = `${apiName} - ${data.name}`;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = props;

  const created = await retryWithBackoff(() => objectsApi.createObject(payload), { maxRetries: 3 });
  return gobodoToValidationRule(created);
}

export async function updateValidationRule(
  id: string,
  updates: Partial<Pick<ValidationRule, 'name' | 'description' | 'conditions' | 'logicOperator' | 'errorMessage' | 'errorLocation' | 'errorFieldId' | 'isActive'>>
): Promise<ValidationRule> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  const existing = await retryWithBackoff(() => objectsApi.getObjectById(parseInt(id)), {
    maxRetries: 3,
  });
  const current = gobodoToValidationRule(existing);

  const merged: ValidationRule = { ...current, ...updates };
  const props = validationRuleToGobodoProps(merged);

  const payload = new GobodoApi.GraphObjectPayload();
  payload.typekey = OBJECT_KEY;
  payload.typeid = (existing as any).typeid;
  payload.label = existing.label;
  payload.graphs = [GobodoConfig.graph()];
  payload.properties = { ...(existing.properties || existing.data || {}), ...props };

  const updated = await retryWithBackoff(() => objectsApi.updateObject(parseInt(id), payload), {
    maxRetries: 3,
  });
  return gobodoToValidationRule(updated);
}

export async function deleteValidationRule(id: string): Promise<void> {
  if (!USE_GOBODO_API) {
    throw new Error('Gobodo API not enabled. Set REACT_APP_USE_GOBODO_API=true');
  }

  const client = getGobodoClient();
  const objectsApi = new GobodoApi.GraphObjectsApi(client);

  try {
    await retryWithBackoff(() => objectsApi.deleteObject(parseInt(id)), { maxRetries: 3 });
  } catch (error: any) {
    const gobodoError = handleGobodoError(error, { operation: 'deleteValidationRule', objectId: id });
    if (gobodoError) {
      logGobodoError(gobodoError);
      throw gobodoError;
    }
    throw error;
  }
}
